import { withSparseSeriesFallback } from "./format";

/** Per-view card metrics. First entry is the default. */
export const METRICS_BY_VIEW = {
  traffic: [
    { id: "visits", label: "Visits", format: "number", unit: "visits" },
    { id: "pageviews", label: "Pageviews", format: "number", unit: "pageviews" },
    { id: "pages_per_visit", label: "Pages / visit", format: "decimal" },
  ],
  organic: [
    { id: "search", label: "Search visits", format: "number", unit: "visits" },
    { id: "ai", label: "AI visits", format: "number", unit: "visits" },
  ],
  speed: [
    { id: "good_lcp", label: "Good LCP %", format: "percent" },
    { id: "lcp", label: "LCP", format: "ms" },
    { id: "inp", label: "INP", format: "ms" },
    { id: "cls", label: "CLS", format: "cls" },
  ],
  audits: [
    { id: "pass_rate", label: "Pass rate", format: "percent" },
    { id: "pass", label: "Pass", format: "number" },
    { id: "warn", label: "Warn", format: "number" },
    { id: "fail", label: "Fail", format: "number" },
  ],
  growth: [
    { id: "pages", label: "Pages", format: "number", unit: "pages" },
    { id: "deploys", label: "Deploys", format: "number", unit: "deploys" },
  ],
  indexing: [],
};

export const defaultMetric = (view) => METRICS_BY_VIEW[view]?.[0]?.id || null;

export const metricOptions = (view) => METRICS_BY_VIEW[view] || [];

export const getMetricDef = (view, metricId) => {
  const options = metricOptions(view);
  return options.find((item) => item.id === metricId) || options[0] || null;
};

/** Resolve the display value / previous / chart series for a site + metric. */
export const resolveSiteMetric = (site, view, metricId) => {
  const def = getMetricDef(view, metricId);
  if (!def || !site) {
    return { value: 0, previous: null, series: [], format: "number", unit: null, label: "" };
  }
  const extra = site.extra || {};
  const series = site.series || [];

  if (view === "traffic") {
    if (def.id === "pageviews") {
      const value = extra.pageviews || 0;
      return {
        ...base(def),
        value,
        previous: extra.previous_pageviews || 0,
        series: withSparseSeriesFallback(
          series.map((point) => ({
            date: point.date,
            value: seriesPageviews(point),
          })),
          value,
        ),
      };
    }
    if (def.id === "pages_per_visit") {
      const visits = site.value || 0;
      const pageviews = extra.pageviews || 0;
      const prevVisits = site.previous || 0;
      const prevPageviews = extra.previous_pageviews || 0;
      return {
        ...base(def),
        value: visits ? pageviews / visits : 0,
        previous: prevVisits ? prevPageviews / prevVisits : 0,
        series: series.map((point) => {
          const v = point.visits ?? point.value ?? 0;
          const pv = seriesPageviews(point);
          return { date: point.date, value: v ? pv / v : 0 };
        }),
      };
    }
    const value = site.value || 0;
    return {
      ...base(def),
      value,
      previous: site.previous || 0,
      series: withSparseSeriesFallback(
        series.map((point) => ({
          date: point.date,
          value: point.visits ?? point.value ?? 0,
        })),
        value,
      ),
    };
  }

  if (view === "organic") {
    if (def.id === "ai") {
      const value = extra.ai_visits || 0;
      return {
        ...base(def),
        value,
        previous: extra.ai_previous || 0,
        series: withSparseSeriesFallback(
          series.map((point) => ({ date: point.date, value: point.ai ?? 0 })),
          value,
        ),
      };
    }
    const value = extra.search_visits ?? site.value ?? 0;
    return {
      ...base(def),
      value,
      previous: extra.search_previous ?? site.previous ?? 0,
      series: withSparseSeriesFallback(
        series.map((point) => ({
          date: point.date,
          value: point.search ?? point.value ?? 0,
        })),
        value,
      ),
    };
  }

  if (view === "speed") {
    if (def.id === "lcp" || def.id === "inp" || def.id === "cls") {
      return {
        ...base(def),
        value: extra[def.id],
        previous: null,
        series: series
          .filter((point) => point[def.id] != null)
          .map((point) => ({ date: point.date, value: point[def.id] })),
      };
    }
    return {
      ...base(def),
      value: site.value || 0,
      previous: site.previous || 0,
      series: series.map((point) => ({ date: point.date, value: point.value ?? 0 })),
    };
  }

  if (view === "audits") {
    if (def.id === "pass" || def.id === "warn" || def.id === "fail") {
      return {
        ...base(def),
        value: extra[def.id] ?? 0,
        previous: null,
        series: series.map((point) => ({
          date: point.date,
          value: point[def.id] ?? 0,
          pass: point.pass,
          warn: point.warn,
          fail: point.fail,
        })),
      };
    }
    return {
      ...base(def),
      value: site.value || 0,
      previous: site.previous || 0,
      series,
    };
  }

  if (view === "growth") {
    if (def.id === "deploys") {
      return {
        ...base(def),
        value: extra.deploy_count || 0,
        previous: null,
        series: series.map((point) => ({ date: point.date, value: point.value ?? 0 })),
      };
    }
    return {
      ...base(def),
      value: site.value || 0,
      previous: null,
      series: [],
    };
  }

  return {
    ...base(def),
    value: site.value || 0,
    previous: site.previous || 0,
    series: [],
  };
};

const base = (def) => ({
  id: def.id,
  label: def.label,
  format: def.format,
  unit: def.unit || null,
});

/** Prefer explicit pageviews; fall back to visits when older series payloads omit it. */
const seriesPageviews = (point) => {
  if (point && Object.prototype.hasOwnProperty.call(point, "pageviews")) {
    return point.pageviews ?? 0;
  }
  return point?.visits ?? point?.value ?? 0;
};
